import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  Box, 
  Typography, 
  Button,  
  CircularProgress, 
  Tooltip, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
} from '@mui/material';
import { 
  AddCircleOutline, 
  Edit,
  Delete,
  DeleteForever,
  Visibility,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import api from 'state/axios';
import { LAYOUT_HEIGHT } from 'state/constants';
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';


const TatTemplates = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user); 
  const theme = useTheme();
  const primaryDark = theme.palette.primary.dark;
  const greenDeep = theme.palette.green.deep;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canCreate = hasPermission(user?.permissions, "TatTemplates", "create");
  const canView = hasPermission(user?.permissions, "TatTemplates", "view");
  const canEdit = hasPermission(user?.permissions, "TatTemplates", "update");
  const canDelete = hasPermission(user?.permissions, "TatTemplates", "delete");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [tatTemplates, setTatTemplates] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'single' or 'bulk'
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);


  useEffect(() => {
    const handleTatTemplates = async () => {
      try {
        setError(null);
        setLoading(true);  

        const res = await api.get('/tattemplates');        
        setTatTemplates(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };  
    handleTatTemplates();
  }, []); 

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      ...isMobile ? { width: 60 } : { flex: 0.5 },
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "acTypeCodes",
      headerName: "ACTypeCodes",
      ...isMobile ? { width: 120 } : { flex: 1.5 },
      renderCell: (params) => (params.value?.length ? params.value.join(", ") : "-"),
    },
    {
      field: "acRegs",
      headerName: "ACRegs",
      ...isMobile ? { width: 120 } : { flex: 1.5 },
      renderCell: (params) => (params.value?.length ? params.value.join(", ") : "-"),
    },
    {
      field: "airportCodes",
      headerName: "Airports",
      ...isMobile ? { width: 120 } : { flex: 1.5 },
      renderCell: (params) => (params.value?.length ? params.value.join(", ") : "-"),
    },
    {
      field: "sectors",
      headerName: "Sectors",
      ...isMobile ? { width: 120 } : { flex: 1.5 },
      renderCell: (params) => (params.value?.length ? params.value.join(", ") : "-"),
    },
    {
      field: "tasks",
      headerName: "Tasks",
      ...isMobile ? { width: 100 } : { flex: 1.5 },
      renderCell: (params) => {
        const tasks = params.value || [];
        if (!tasks.length) return "-";
        return (
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                {tasks.map((e, i) => (
                  <Typography key={i} variant="body2">
                    {`${e.taskCode} (${e.duration} min)`}
                  </Typography>
                ))}
              </Box>
            }
            arrow
          >
            <Typography 
              variant="body2" 
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "help",
                height: "100%", 
              }}
            >
              {`${tasks.length} tasks`}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "isActive",
      headerName: "IsActive",
      ...isMobile ? { width: 90 } : { flex: 1 },
      // renderCell: (params) => params.value ? "Yes" : "No",
    },
    // {
    //   field: "updatedAt",
    //   headerName: "LastUpdated",
    //   ...isMobile ? { width: 160 } : { flex: 1.5 },
    //   renderCell: (params) => new Date(params.value).toLocaleString(),
    // },
  ], [isMobile]);

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      filterable: false,      
      sortable: false,
      width: 125,      
      renderCell: (params) => {        
        return (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center"
            // gap={1}
            sx={{ width: '100%', height: '100%' }}
          >         
            <Tooltip title="View Tasks">
              <IconButton
                aria-label="View Tasks"
                sx={{
                  color: primaryDark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': { color: greenDeep },
                }}
                onClick={() => {
                  const tasks = params.row.tasks || [];
                  if (!tasks.length) {
                    toast.error("No tasks found for this template.");
                    return;
                  }
                  if (!canView) return;
                  setSelectedTasks(tasks);
                  setTaskDialogOpen(true);
                }}
              >
                <Visibility />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Edit">
              <IconButton
                aria-label="Edit"
                sx={{
                  color: primaryDark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': { color: greenDeep },
                }}
                onClick={() => {
                  if (!canEdit) return;
                  navigate(`/tattemplates/${params.row._id}`)
                }}
              >
                <Edit />
              </IconButton>
            </Tooltip>
          
            <Tooltip title="Delete">
              <IconButton
                aria-label="Delete"
                sx={{
                  color: primaryDark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': { color: greenDeep },
                }}
                onClick={() => {
                  if (!canDelete) return;
                  handleDeleteClick(params.row._id);                  
                }}
              >
                <DeleteForever />
              </IconButton>                
            </Tooltip>          
          </Box>
        );
      },
    },
  ];

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, tatTemplates);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        
        await api.delete(`/tattemplates/delete/${idToDelete}`);

        setTatTemplates(tatTemplates.filter((item) => item._id !== idToDelete));
        toast.success('Turnaround template deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, tatTemplates);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }

        const url = '/tattemplates/soft-delete';
        const payload = { ids: selectedIds };        
        const res = await api.patch(url, payload);        
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        
        setTatTemplates(tatTemplates.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete tattemplates!");
    }
  }, [
    canDelete, 
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    tatTemplates,
  ]);
 

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="TAT Templates" 
          subtitle={`${loading ? 'Loading...' : 'List of TAT Templates'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, tatTemplates)}
                    onClick={handleBulkDelete}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}

            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate("/tattemplates/new")}>
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          sx={{
            "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
          }}
        >
          <Box
            height={LAYOUT_HEIGHT}  
            sx={{ gridColumn: "span 12" }}    
          >                
            {isMobile ? (
              <CardList
                rows={tatTemplates}
                primaryField="acTypeCodes"
                fields={[
                  { label: "Airports", key: "airportCodes" },
                  { label: "Sectors", key: "sectors" },
                  { label: "Is Active", key: "isActive" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/tattemplates/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
            <DataGridComponent
              rows={tatTemplates || []}
              columns={columns.concat(actionColumn)}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              loading={loading}
              props={{
                checkboxSelection: true,
                disableRowSelectionOnClick: true,
                onRowSelectionModelChange: setRowSelectionModel,
                rowSelectionModel,
                getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
                initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                sx: {
                  "& .MuiDataGrid-row.isActive-false": {
                    backgroundColor: `${theme.palette.primary.light} !important`,  
                  },
                },
              }}
            />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />

      {taskDialogOpen && (
        <Dialog
          open={taskDialogOpen}
          onClose={() => setTaskDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h5">Tasks</Typography>
          </DialogTitle>
          <DialogContent dividers>
            {selectedTasks.length === 0 ? (
              <Typography>No tasks found.</Typography>
            ) : (
              <Box>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr >
                      <th style={{ padding: 8, borderBottom: "1px solid #ccc" }}>TaskCode</th>
                      <th style={{ padding: 8, borderBottom: "1px solid #ccc" }}>TaskName</th>
                      <th style={{ padding: 8, borderBottom: "1px solid #ccc" }}>Preceding</th>
                      <th style={{ padding: 8, borderBottom: "1px solid #ccc" }}>Duration (min)</th>
                      <th style={{ padding: 8, borderBottom: "1px solid #ccc" }}>StartTime (min)</th>
                      <th style={{ padding: 8, borderBottom: "1px solid #ccc" }}>Priority</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTasks.map((task, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: 8 }}>{task.taskCode}</td>
                        <td style={{ padding: 8 }}>{task.taskName || "-"}</td>
                        <td style={{ padding: 8 }}>
                          {task.preceding?.length ? task.preceding.join(", ") : "-"}
                        </td>
                        <td style={{ padding: 8, textAlign: "right" }}>{task.duration ?? 0}</td>
                        <td style={{ padding: 8, textAlign: "right" }}>{task.startTime ?? 0}</td>
                        <td style={{ padding: 8, textAlign: "right" }}>{task.priority ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              color="primary"
              variant="outlined" 
              onClick={() => setTaskDialogOpen(false)} 
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

    </Box>
  );
};

export default TatTemplates;
